import _ from "lodash";
import Reflux from "reflux";
import AssetActions from "../actions/AssetActions";
import AssetLibraryActions from "../actions/AssetLibraryActions";
import UIActions from "../actions/UIActions";
import AppConfig from "../config/app";
import AppConstants from "../constants/AppConstants";
import { ASSET_FOLDERS, USER_ASSETS } from "../constants/QueryKeys";
import { getAssetFoldersData } from "../hooks/query/useAssetFolders";
import { getUserAssetsData, sortUserAssets } from "../hooks/query/useUserAssets";
import AssetUploads from "../stores/AssetUploadStore";
import { checkAndHandleMaxGalleryWarning } from "../utils/AppUtils";
import AssetUtils from "../utils/AssetUtils";
import { subscribeToQuery } from "../utils/queryCacheBridge";

const {
  ASSET_LIBRARY_MODE_MANAGE,
  MODAL_ASSET_LIBRARY,
  ASSET_LIBRARY_MODE_CHOOSER,
  ASSET_LIBRARY_MODE_GALLERY_CHOOSER,
  ASSET_LIBRARY_MODE_VIEW,
  ASSET_LIBRARY_TAB_FILES,
  ASSET_LIBRARY_TAB_STOCK,
  ASSET_TYPE_IMAGE,
  ASSET_TYPE_VIDEO,
  ASSET_TYPE_LOTTIE,
  PANEL_SECTION_ELEMENT_SVG,
  ASSET_TYPE_IMAGE_EXTS,
  PANEL_SECTION_ELEMENT_GRAPHIC,
  ASSET_TYPE_VIDEO_EXTS,
  PANEL_SECTION_ELEMENT_VIDEO,
  UPLOAD_STATUS_ERROR,
} = AppConstants;

var AssetLibraryStore = Reflux.createStore({
  storeName: "AssetLibraryStore",
  listenables: [AssetLibraryActions],

  init() {
    this._unsubscribeUserAssets = subscribeToQuery(
      [USER_ASSETS],
      this._onUserAssetsChange.bind(this),
    );
    this._unsubscribeAssetFolders = subscribeToQuery(
      [ASSET_FOLDERS],
      this._onAssetFoldersChange.bind(this),
    );
    this.listenTo(AssetUploads, this._onAssetUploadsChange);
    this.listenTo(UIActions.toggleAssetChooser, this._onToggle);
    this.listenTo(AssetActions.deleteFolder, this._onDeleteFolder);

    this.assets = []; //unfiltered list
    this.folders = [];
    this.uploading = []; //unfiltered list
    this.selected = [];
    this.lastSelection = null;

    this.lastGallerySelection = null;

    this.originalGalleryAssets = [];

    this.isOpen = false;

    this.filterProps = { folder_id: null };

    this.state = {
      selectedFolder: null,

      assets: [], //current assets visible
      uploading: [], //current uploads visible
      numUploads: 0,

      title: "Choose Asset",
      btnLabel: "Add To Page",
      activeTab: ASSET_LIBRARY_TAB_FILES,

      mode: ASSET_LIBRARY_MODE_MANAGE,
      galleryAssets: [], //list used to hold gallery assets when in gallery chooser mode
      galleryItemsSelected: 0,
      galleryEditEnabled: false,
      galleryExpanded: false,

      filter: false, //asset_type
      filterExts: false, //extensions [array]
      search: "",
      sortBy: null,

      allowUpload: true,
      allowFilter: true,
      allowSearch: true,
      allowSort: true,
      allowFolderCreate: true,
      allowFolderMoveTo: true,
      allowDelete: true,
      allowAcceptList: false,

      primaryActionBtnEnabled: false,
      cancelBtnEnabled: false,

      onSelect: false,
      // Receives a Pexels photo; outside gallery mode, the Stock Photos tab needs it.
      stockPhotoOnSelect: false,

      numSelected: 0,

      dialog: null,
    };
  },

  _onUserAssetsChange() {
    this.assets = getUserAssetsData()?.assets ?? [];

    this.state.assets = this._filterAssetList(this.assets);

    this._initSelected(this.selected);

    this.trigger();
  },

  // Folder-only changes skip the full asset re-filter.
  _onAssetFoldersChange() {
    this.folders = getAssetFoldersData()?.folders ?? [];
    this.state.folders = this.folders;

    this.trigger();
  },

  _onAssetUploadsChange() {
    var uploadInfo = AssetUploads.getInfo();

    this.uploading = uploadInfo.assets;

    this.state.uploading = this.uploading;
    this.state.numUploads = uploadInfo.numUploads;

    this.trigger();
  },

  _onToggle(settings) {
    if (this.isOpen) {
      UIActions.modalClose(MODAL_ASSET_LIBRARY);
    } else {
      this._reset();

      const demoMode = AppConfig.demoMode;
      var config = {
        onHide: UIActions.toggleAssetChooser,
        demoMode: demoMode,
      };

      if (settings) {
        config.overlay = _.get(settings, "overlay", false);

        if (_.has(settings, "selected")) {
          this._initSelected(settings.selected);
          _.unset(settings, "selected");
        }

        if (_.has(settings, "galleryAssets")) {
          this._copyGalleryAssets(settings.galleryAssets);
        }

        _.merge(this.state, settings);
      }

      this._configByMode();
      this._updateFilterProps();
      this._updateAssetLists();

      UIActions.modalOpen(MODAL_ASSET_LIBRARY, config);
    }

    this.isOpen = !this.isOpen;
  },

  _onDeleteFolder(folder) {
    if (this.state.selectedFolder !== null) {
      if (this.state.selectedFolder.folder_id == folder.folder_id) {
        this.state.selectedFolder = null;
        this._updateFilterProps();
        this._resetSelected();
      }
    }
  },

  /**
   * @param {string} tab ASSET_LIBRARY_TAB_FILES or ASSET_LIBRARY_TAB_STOCK
   */
  onSelectTab(tab) {
    // The expanded organizer hides the stock tab without changing activeTab.
    const leavingOrganizer = tab === ASSET_LIBRARY_TAB_STOCK && this.state.galleryExpanded;
    if (tab === this.state.activeTab && !leavingOrganizer) return;

    if (leavingOrganizer) this._contractGallery();

    this.state.activeTab = tab;
    this.state.dialog = null;
    this.trigger();
  },

  onShowCreateFolder() {
    this.state.dialog = {
      name: "folderCreate",
    };

    this.trigger();
  },

  onShowRenameFolder(folder) {
    this.state.dialog = {
      name: "folderRename",
      payload: folder,
    };

    this.trigger();
  },

  onShowFolderChooser() {
    this.state.dialog = {
      name: "folderChooser",
    };

    this.trigger();
  },

  onAcceptMoveToFolder(folder) {
    var selectedFolder = this.state.selectedFolder;
    var selected = this._filterUploadErrors(this.selected);

    //Don't move things if move to folder is the same as the selected folder
    if (selectedFolder !== folder && !_.isEmpty(selected)) {
      AssetActions.moveToFolder(selected, folder);
    }

    this._resetSelected();
  },

  onAddSelectedToList(data) {
    const d = data?.data || data;
    let targetIndex = _.get(d, ["target", "index"]);

    //get selected assets from visible list so they get added in order
    const selected = this.state.assets.filter((asset) => asset.selected === true);

    if (_.isNil(targetIndex)) {
      targetIndex = this.state.galleryAssets.length;
    }

    const assets = _.cloneDeep(this.state.galleryAssets);
    const toAdd = _.map(selected, AssetUtils.getGalleryImageAsset);

    assets.splice.apply(assets, [targetIndex, 0].concat(toAdd));

    this.state.galleryAssets = assets;

    this._resetSelected();

    this.trigger();
    checkAndHandleMaxGalleryWarning(this.state.galleryAssets.length);
  },

  /**
   * Appends already gallery-shaped items, e.g. stock photos, to the end of the list.
   * @param {Array<Object>} items
   */
  onAddGalleryItems(items) {
    this.state.galleryAssets = this.state.galleryAssets.concat(_.cloneDeep(items));

    this.trigger();
    checkAndHandleMaxGalleryWarning(this.state.galleryAssets.length);
  },

  onRemoveSelectedFromList() {
    _.remove(this.state.galleryAssets, { selected: true });

    this._updateGalleryItemsSelected();

    this.trigger();
  },

  onReorderList(moveData) {
    const assets = _.cloneDeep(this.state.galleryAssets);

    //look at item being moved to see if it is selected, if not just select it first
    const selectedItem = assets[moveData.item.index];
    if (!_.has(selectedItem, "selected")) {
      selectedItem.selected = true;
    }

    let targetIndex = moveData.target.index;
    if (_.isNil(targetIndex)) {
      targetIndex = this.state.galleryAssets.length;
    }

    for (let i = 0; i < assets.length; i++) {
      if (_.has(assets[i], "selected")) {
        if (i < targetIndex) {
          targetIndex--;
        }
      }
    }

    if (targetIndex >= 0) {
      const selected = _.remove(assets, { selected: true }); //remove selected from list
      assets.splice.apply(assets, [targetIndex, 0].concat(selected));

      this.state.galleryAssets = assets;
      this._updateGalleryItemsSelected();

      this.trigger();
    }
  },

  onSelectListItem(selection) {
    const asset = selection.asset;
    const isSelected = _.get(asset, "selected", false);
    const numSelected = this.state.galleryItemsSelected;
    let updated = true;

    if ("mousedown" == selection.event) {
      if (selection.multiSelect) {
        if (isSelected) {
          this._removeFromGallerySelection([asset]);
        } else {
          this._addToGallerySelection([asset]);
        }
      } else if (selection.rangeSelect) {
        const assets = this.state.galleryAssets;
        let lastSelectedIndex = 0;
        const selectionIndex = selection.index;

        if (this.lastGallerySelection !== null) {
          const lastAsset = this.lastGallerySelection.asset;
          lastSelectedIndex = _.indexOf(assets, lastAsset);
        }

        const toSelect = this._getRangeSelection(assets, selectionIndex, lastSelectedIndex);

        this._addToGallerySelection(toSelect);
      } else if (!isSelected) {
        this._resetGallerySelected();
        this._addToGallerySelection([asset]);
      } else {
        updated = false;
      }
    } else if ("mouseup" == selection.event) {
      if (!selection.multiSelect && !selection.rangeSelect && isSelected && numSelected > 1) {
        this._resetGallerySelected();
        this._addToGallerySelection([asset]);
      } else {
        updated = false;
      }
    }

    if (updated) {
      this.lastGallerySelection = selection;
      this._updateGalleryItemsSelected();
      this.trigger();
    }
  },

  onExpandGallery() {
    this.state.galleryExpanded = true;
    this.state.allowFilter = false;
    this.state.allowUpload = false;
    this.state.allowSort = false;
    this.state.allowSearch = false;
    this.trigger();
  },

  onContractGallery() {
    this._contractGallery();
    this.trigger();
  },

  _contractGallery() {
    this.state.galleryExpanded = false;
    this.state.allowFilter = true;
    this.state.allowUpload = true;
    this.state.allowSort = true;
    this.state.allowSearch = true;
  },

  _addAssetToGallery(asset) {
    this.state.galleryAssets.push(AssetUtils.getGalleryImageAsset(asset));
  },

  _updateGalleryItemsSelected() {
    // Selection flags are set in place, so hand out a new array: GalleryEditList is memoized
    // and would otherwise skip re-rendering when only the selected item changes.
    this.state.galleryAssets = [...this.state.galleryAssets];
    var numSelected = this.state.galleryAssets.filter((asset) => asset.selected === true).length;

    this.state.galleryItemsSelected = numSelected;
  },

  _removeFromGallerySelection(items) {
    _.forEach(items, (item) => {
      _.unset(item, "selected");
    });
  },

  _addToGallerySelection(items) {
    _.forEach(items, (item) => {
      item.selected = true;
    });
  },

  _resetGallerySelected() {
    _.forEach(this.state.galleryAssets, (item) => {
      _.unset(item, "selected");
    });

    this.lastGallerySelection = null;
  },

  _copyGalleryAssets(assets) {
    this.originalGalleryAssets = _.cloneDeep(assets);
  },

  _filterAssetList(assets) {
    var filtered = assets;

    if (!_.isEmpty(this.filterProps)) {
      var assetProps = _.pick(this.filterProps, ["folder_id", "asset_type"]);
      var assetExts = _.get(this.filterProps, "exts", false);

      const isLottieFilter = assetProps.asset_type === AppConstants.ASSET_TYPE_LOTTIE;
      if (isLottieFilter) {
        delete assetProps.asset_type;
      }

      filtered = filtered.filter((asset) => {
        const matchesProps = _.iteratee(assetProps)(asset);
        const matchesExts = assetExts ? _.indexOf(assetExts, asset.ext) >= 0 : true;
        const matchesLottie = isLottieFilter ? AssetUtils.isLottieAsset(asset) : true;
        return matchesProps && matchesExts && matchesLottie;
      });
    }

    var search = _.toLower(this.state.search);
    if (!_.isEmpty(search) && _.size(search) >= 2) {
      filtered = filtered.filter((asset) => _.toLower(asset.asset_name).includes(search));
    }

    return filtered;
  },

  //Applies filter and folder to the current list of assets
  _updateAssetLists() {
    this.state.assets = this._filterAssetList(this.assets);
  },

  _updateFilterProps() {
    var filterProps = { folder_id: null };

    if (this.state.selectedFolder !== null) {
      filterProps.folder_id = this.state.selectedFolder.folder_id;
    }

    if (this.state.filter) {
      filterProps.asset_type = this.state.filter;
    }

    if (this.state.filterExts) {
      filterProps.exts = this.state.filterExts;
    }

    this.filterProps = filterProps;
  },

  _reset() {
    this._resetSelected();

    this.state.mode = ASSET_LIBRARY_MODE_MANAGE;

    this.state.galleryAssets = [];
    this.state.galleryItemsSelected = false;
    this.state.galleryEditEnabled = false;
    this.state.galleryExpanded = false;
    this.lastGallerySelection = null;
    this.originalGalleryAssets = [];

    this.state.onSelect = false;
    this.state.stockPhotoOnSelect = false;
    this.state.activeTab = ASSET_LIBRARY_TAB_FILES;

    this.state.cancelBtnEnabled = false;
    this.state.primaryActionBtnEnabled = false;
    this.state.allowDelete = true;
    this.state.allowFilter = true;
    this.state.allowSearch = true;
    this.state.allowSort = true;
    this.state.allowMoveToFolder = true;
    this.state.allowUpload = true;

    this.state.filter = false;
    this.state.filterExts = false;

    this.state.dialog = null;
  },

  _configByMode() {
    //override defaults with values specific to chooser mode

    switch (this.state.mode) {
      case ASSET_LIBRARY_MODE_CHOOSER:
        this.state.allowFilter = false;
        this.state.allowDelete = false;
        this.state.allowMoveToFolder = false;
        break;
      case ASSET_LIBRARY_MODE_GALLERY_CHOOSER:
        this.state.allowFilter = false;
        this.state.allowDelete = false;
        this.state.allowMoveToFolder = false;
        this.state.galleryEditEnabled = true;
        this.state.galleryExpanded = false;
        this.state.primaryActionBtnEnabled = true;
        this.state.cancelBtnEnabled = true;
        break;
      case ASSET_LIBRARY_MODE_VIEW:
        break;
    }
  },

  //Used when asset list is refreshed to show assets as selected
  _initSelected(initSelected) {
    var selected = [];
    _.forEach(
      initSelected,
      _.bind(function (selAsset) {
        var asset = _.find(this.assets, { key: selAsset.key });
        if (asset) {
          selected.push(asset);
          asset.selected = true;
        }
      }, this),
    );

    this.selected = selected;
  },

  _updateAfterSelectionChange() {
    switch (this.state.mode) {
      case ASSET_LIBRARY_MODE_MANAGE: {
        // if only non-image or non-video assets are selected, hide the action button
        const hideActionBtn = this.selected.every(
          (asset) =>
            asset.asset_type !== ASSET_TYPE_IMAGE &&
            asset.asset_type !== ASSET_TYPE_VIDEO &&
            asset.asset_type !== ASSET_TYPE_LOTTIE,
        );
        this.state.primaryActionBtnEnabled =
          this.state.numSelected <= 0 || hideActionBtn ? false : true;
        break;
      }
    }
  },

  _resetSelected() {
    _.forEach(this.selected, (asset) => {
      _.unset(asset, "selected");
    });

    this.selected = [];
    this.lastSelection = null;
    this.state.numSelected = 0;

    this._updateAfterSelectionChange();
  },

  _unselectAsset(asset) {
    //compare keys because assets in design do not have an ID
    _.remove(this.selected, { key: asset.key });
    _.unset(asset, "selected");

    this.state.numSelected = this.selected.length;

    this._updateAfterSelectionChange();
  },

  _selectAsset(asset) {
    this.selected.push(asset);
    asset.selected = true;

    this.state.numSelected = this.selected.length;

    this._updateAfterSelectionChange();
  },

  onSelectFolder(folder) {
    if (folder !== this.state.selectedFolder) {
      this.state.selectedFolder = folder;

      this._resetSelected();

      this._updateFilterProps();
      this._updateAssetLists();

      this.trigger();
    }
  },

  onUpdateFilter(filter) {
    this.state.filter = filter;

    this._resetSelected();

    this._updateFilterProps();
    this._updateAssetLists();

    this.trigger();
  },

  onUpdateSearch(search) {
    this.state.search = _.trim(search);

    this._resetSelected();
    this._updateAssetLists();

    this.trigger();
  },

  onUpdateSort(sort) {
    //triggers a one time sort on the UserAssetList
    if (!_.isEmpty(sort)) {
      this.state.sortBy = sort;
      sortUserAssets(sort);
    }

    this.trigger();
  },

  onUpdateState(props) {
    _.merge(this.state, props);
    this.trigger();
  },

  onSelectAsset(selection) {
    switch (this.state.mode) {
      case ASSET_LIBRARY_MODE_MANAGE:
      case ASSET_LIBRARY_MODE_VIEW:
        this._handleManageModeSelection(selection);
        break;
      case ASSET_LIBRARY_MODE_CHOOSER:
        this._handleChooserModeSelection(selection);
        break;
      case ASSET_LIBRARY_MODE_GALLERY_CHOOSER:
        this._handleGalleryModeSelection(selection);
        break;
    }
  },

  /**
   * Returns the a list of items that should be selected
   */
  _getRangeSelection(list, selectionIndex, lastSelectedIndex) {
    lastSelectedIndex = lastSelectedIndex <= 0 ? 0 : lastSelectedIndex;
    var minIndex = Math.min(selectionIndex, lastSelectedIndex);
    var maxIndex = Math.max(selectionIndex, lastSelectedIndex);

    var toSelect = [];

    if (minIndex == 0 && maxIndex + 1 == list.length) {
      toSelect = list;
    } else {
      toSelect = _.slice(list, minIndex, maxIndex + 1);
    }

    return toSelect;
  },

  _rangeSelect(selection) {
    var assets = this.state.assets;
    var lastSelectedIndex = 0;
    var selectionIndex = selection.index;

    if (this.lastSelection !== null) {
      var lastAsset = this.lastSelection.asset;
      lastSelectedIndex = _.indexOf(assets, lastAsset);
    }

    var toSelect = this._getRangeSelection(assets, selectionIndex, lastSelectedIndex);

    _.forEach(
      toSelect,
      _.bind(function (asset) {
        if (!_.has(asset, "selected")) {
          this._selectAsset(asset);
        }
      }, this),
    );
  },

  _handleManageModeSelection(selection) {
    const asset = selection.asset;
    const isSelected = _.has(asset, "selected");
    const numSelected = this.state.numSelected;
    let updated = true;

    if (selection.event == "mouseup") {
      if (!selection.multiSelect && !selection.rangeSelect && isSelected && numSelected > 1) {
        this._resetSelected();
        this._selectAsset(asset);
      } else {
        updated = false;
      }
    } else {
      if (selection.rangeSelect) {
        this._rangeSelect(selection);
      } else if (selection.altAction) {
        //Added element to stage and close
        AssetActions.createElement({ asset: asset });
        UIActions.toggleAssetChooser(null);
      } else if (selection.multiSelect || selection.source == "check") {
        if (isSelected) {
          this._unselectAsset(asset);
        } else {
          this._selectAsset(asset);
        }
      } else if (!isSelected) {
        this._resetSelected();
        this._selectAsset(asset);
      } else {
        updated = false;
      }
    }

    if (updated) {
      this.lastSelection = selection;
      this.trigger();
    }
  },

  _handleGalleryModeSelection(selection) {
    const asset = selection.asset;
    const isSelected = _.has(asset, "selected");
    const numSelected = this.state.numSelected;
    let updated = true;

    if (selection.event == "mouseup") {
      if (!selection.multiSelect && !selection.rangeSelect && isSelected && numSelected > 1) {
        this._resetSelected();
        this._selectAsset(asset);
      } else {
        updated = false;
      }
    } else {
      if (selection.rangeSelect) {
        this._rangeSelect(selection);
      } else if (selection.altAction) {
        this._addAssetToGallery(asset);
        this._resetSelected();
      } else if (selection.multiSelect || selection.source == "check") {
        if (isSelected) {
          this._unselectAsset(asset);
        } else {
          this._selectAsset(asset);
        }
      } else if (!isSelected) {
        this._resetSelected();
        this._selectAsset(asset);
      } else {
        updated = false;
      }
    }

    if (updated) {
      this.lastSelection = selection;
      this.trigger();
    }
  },

  _handleChooserModeSelection(selection) {
    if (selection.event == "mousedown") {
      if (this.state.onSelect !== false) {
        this.state.onSelect(selection.asset);
      }

      UIActions.toggleAssetChooser(null);
    }
  },

  /**
   * @param {{onAccepted?: (assets: Array<Object>) => void}} [options] gallery mode only:
   *   runs after onSelect has dispatched its write, so follow-up actions queue behind it
   */
  onAcceptAssetSelection({ onAccepted } = {}) {
    switch (this.state.mode) {
      case ASSET_LIBRARY_MODE_GALLERY_CHOOSER:
        this._handleGalleryAccept(onAccepted);
        break;
      case ASSET_LIBRARY_MODE_MANAGE:
        this._handleManageAccept();
        break;
    }
  },

  _handleGalleryAccept(onAccepted) {
    var assets = _.cloneDeep(this.state.galleryAssets);

    //clear selected property
    _.forEach(assets, (item) => {
      _.unset(item, "selected");
    });

    if (this.state.onSelect !== false) {
      this.state.onSelect(assets);
    }

    onAccepted?.(assets);
  },

  _handleManageAccept() {
    var selected = this._filterUploadErrors(_.cloneDeep(this.selected));
    if (this.state.onSelect !== false) {
      this.state.onSelect(selected);
    } else {
      AssetActions.createElements(selected);

      if (selected.length === 1) {
        const asset = selected[0];
        if (asset.ext === "svg") {
          UIActions.toggleElementPropertiesSection({
            name: PANEL_SECTION_ELEMENT_SVG,
          });
        } else if (ASSET_TYPE_IMAGE_EXTS.includes(asset.ext)) {
          UIActions.toggleElementPropertiesSection({
            name: PANEL_SECTION_ELEMENT_GRAPHIC,
          });
        } else if (ASSET_TYPE_VIDEO_EXTS.includes(asset.ext)) {
          UIActions.toggleElementPropertiesSection({
            name: PANEL_SECTION_ELEMENT_VIDEO,
          });
        } else if (AssetUtils.isLottieAsset(asset)) {
          UIActions.toggleElementPropertiesSection({
            name: AppConstants.PANEL_SECTION_ELEMENT_LOTTIE,
          });
        }
      }
    }

    this._resetSelected();
  },

  _filterUploadErrors(selected) {
    return selected.filter((asset) => asset.status !== UPLOAD_STATUS_ERROR);
  },

  onDeleteAssets() {
    var assetsToDelete = this.selected;
    AssetActions.deleteAssets({ assets: assetsToDelete });
    this._resetSelected();
  },

  isFolderEmpty(folder) {
    var hasAssets = _.some(this.assets, { folder_id: folder.folder_id });
    var hasUploads = _.some(this.uploads, { folder_id: folder.folder_id });

    return !hasAssets && !hasUploads;
  },

  getInitialState() {
    return this.state;
  },

  getState() {
    return this.state;
  },
});

export default AssetLibraryStore;
